import React, { useState, useCallback, useEffect, useMemo, useRef } from "react";
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Tooltip,
  Alert,
  Stack,
  Button,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Popover,
} from "@mui/material";
import { ExpandLess, ExpandMore, FilterList, Close } from "@mui/icons-material";
import TextField from "@mui/material/TextField";
import { quickbooksService } from "../../../../api/quickbooks";
import QBErrorDetailsDialog from "../../../dialogs/quickbooks/qbErrorDetailsDialog";
import CustomDateRangeDialog from "../../../dialogs/CustomDateRangeDialog";

const QUICKBOOKS_TASK_KEYS = [
  "general_ledger",
  "general_ledger_cdc",
  "general_ledger_build",
  "general_ledger_fetch",
  "trial_balance",
];

const jobHasFailure = (job) => {
  if (!job) return false;
  const status = (job.status || "").toLowerCase();
  if (status === "failed") return true;
  const results = job.results || {};
  return Object.values(results).some((cust) =>
    QUICKBOOKS_TASK_KEYS.some((key) => {
      const taskStatus = cust?.[key]?.status;
      return taskStatus && !["success", "completed"].includes(taskStatus);
    })
  );
};

const getFailedAccounts = (job) => {
  const results = job?.results || {};
  const ids = new Set();
  Object.entries(results).forEach(([customerId, cust]) => {
    const failed = QUICKBOOKS_TASK_KEYS.some((key) => {
      const taskStatus = cust?.[key]?.status;
      return taskStatus && !["success", "completed"].includes(taskStatus);
    });
    if (failed) {
      ids.add(customerId);
    }
  });
  return Array.from(ids);
};

const getFailedSyncTypes = (job) => {
  const results = job?.results || {};
  const hasGL = Object.values(results).some((cust) =>
    [
      "general_ledger",
      "general_ledger_cdc",
      "general_ledger_build",
      "general_ledger_fetch",
    ].some((key) => {
      const taskStatus = cust?.[key]?.status;
      return taskStatus && !["success", "completed"].includes(taskStatus);
    })
  );
  const hasTB = Object.values(results).some((cust) => {
    const taskStatus = cust?.trial_balance?.status;
    return taskStatus && !["success", "completed"].includes(taskStatus);
  });
  return { hasGL, hasTB };
};

const extractCursor = (url) => {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    return parsed.searchParams.get("cursor");
  } catch (error) {
    return null;
  }
};

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "—";
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  const hour12 = String(hours % 12 || 12).padStart(2, "0");
  return `${month}/${day}/${year}, ${hour12}:${minutes}:${seconds} ${ampm}`;
};

export default function ErrorsTab() {
  // Manage searchTerm internally like UsersTab and AccountsTab
  const [searchTerm, setSearchTerm] = useState("");
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedIds, setExpandedIds] = useState(new Set());
  const nameCacheRef = useRef({});
  const [, setCacheVersion] = useState(0);
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [errorDetailsDialogOpen, setErrorDetailsDialogOpen] = useState(false);
  const [selectedErrorCustomerId, setSelectedErrorCustomerId] = useState(null);
  const [selectedErrorAccountName, setSelectedErrorAccountName] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const rowsPerPageRef = useRef(rowsPerPage);
  useEffect(() => {
    rowsPerPageRef.current = rowsPerPage;
  }, [rowsPerPage]);
  const [pagination, setPagination] = useState({ next: null, previous: null, count: null });
  const [page, setPage] = useState(0);
  const [pageCursors, setPageCursors] = useState([null]);
  const [columnWidths, setColumnWidths] = useState({});
  const [resizing, setResizing] = useState(null);
  const [filterAnchors, setFilterAnchors] = useState({ syncTypes: null, initiated: null, failedAccounts: null });
  const [filters, setFilters] = useState({ syncType: "", initiatedAfter: "", initiatedBefore: "", failedAccount: "" });
  const filtersRef = useRef(filters);
  const [pendingSyncType, setPendingSyncType] = useState("");
  const [initiatedSortOption, setInitiatedSortOption] = useState("");
  const [initiatedDateRange, setInitiatedDateRange] = useState("");
  const [initiatedCustomStartDate, setInitiatedCustomStartDate] = useState("");
  const [initiatedCustomEndDate, setInitiatedCustomEndDate] = useState("");
  const [pendingFailedAccount, setPendingFailedAccount] = useState("");
  const [initiatedCustomDateDialogOpen, setInitiatedCustomDateDialogOpen] = useState(false);
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  const toggleExpanded = (jobId) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(jobId)) {
        next.delete(jobId);
      } else {
        next.add(jobId);
      }
      return next;
    });
  };

  const handleMouseDown = (e, columnField) => {
    e.preventDefault();
    e.stopPropagation();
    setResizing({ field: columnField, startX: e.clientX });
  };

  const openFilter = (key, event) => {
    const anchor = event?.currentTarget || null;
    setPendingSyncType(filtersRef.current.syncType || "");
    if (key === "initiated") {
      // Reset to current filter state when opening
      const currentAfter = filtersRef.current.initiatedAfter || "";
      const currentBefore = filtersRef.current.initiatedBefore || "";
      if (currentAfter && currentBefore) {
        setInitiatedDateRange("custom");
        setInitiatedCustomStartDate(currentAfter);
        setInitiatedCustomEndDate(currentBefore);
      } else {
        setInitiatedDateRange("");
        setInitiatedCustomStartDate("");
        setInitiatedCustomEndDate("");
      }
      setInitiatedSortOption("");
    }
    if (key === "failedAccounts") {
      setPendingFailedAccount(filtersRef.current.failedAccount || "");
    }
    setFilterAnchors((prev) => ({ ...prev, [key]: anchor }));
  };

  const closeFilter = (key) => {
    setFilterAnchors((prev) => ({ ...prev, [key]: null }));
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!resizing) return;
      const delta = e.clientX - resizing.startX;
      setColumnWidths((prev) => ({
        ...prev,
        [resizing.field]: Math.max(100, (prev[resizing.field] || 180) + delta),
      }));
      setResizing({ ...resizing, startX: e.clientX });
    };

    const handleMouseUp = () => setResizing(null);

    if (resizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [resizing]);

  const resolveAccountName = useCallback(
    (job, accountId) => {
      if (!accountId) return "—";
      const accounts = Array.isArray(job?.customer_accounts)
        ? job.customer_accounts.filter(Boolean)
        : [];
      if (accounts.length) {
        const match = accounts.find(
          (acct) => String(acct.id || "") === String(accountId)
        );
        if (match?.name) {
          return match.name;
        }
      }
      const results = job?.results || {};
      return (
        results?.[accountId]?.customerDetails?.name ||
        nameCacheRef.current[String(accountId)] ||
        `Account ${String(accountId).slice(0, 8)}`
      );
    },
    []
  );

  const fetchErrorJobs = useCallback(
    async ({ cursor = null, pageOverride = 0, search = "" } = {}) => {
      setLoading(true);
      setError(null);
      try {
        const accumulated = [];
        let nextCursor = cursor || null;
        let responseNextUrl = null;

      while (
        accumulated.length < rowsPerPageRef.current &&
        (nextCursor !== null || accumulated.length === 0)
      ) {
        const params = { page_size: rowsPerPageRef.current };
        if (nextCursor) {
          params.cursor = nextCursor;
        }

        const searchTrimmed = search && search.trim();
        if (searchTrimmed) {
          params.search = searchTrimmed;
        }
        const failedAccountTrimmed = (filtersRef.current.failedAccount || "").trim();
        if (failedAccountTrimmed) {
          params.account_name = failedAccountTrimmed;
        }

        if (filtersRef.current.initiatedAfter) {
          params.started_after = filtersRef.current.initiatedAfter;
        }
        if (filtersRef.current.initiatedBefore) {
        params.started_before = filtersRef.current.initiatedBefore;
      }

          const response = await quickbooksService.getAllSyncJobs(params);
          responseNextUrl = response?.data?.next || null;
          let failedJobs = (response?.data?.results || []).filter(jobHasFailure);

          if (searchTrimmed) {
            const searchLower = searchTrimmed.toLowerCase();
            failedJobs = failedJobs.filter((job) => {
              const jobId = (job.id || "").toLowerCase();
              if (jobId.includes(searchLower)) {
                return true;
              }
              const failedAccounts = getFailedAccounts(job);
              if (
                failedAccounts.some((accountId) => {
                  const accountName =
                    resolveAccountName(job, accountId).toLowerCase();
                  return accountName.includes(searchLower);
                })
              ) {
                return true;
              }
              const details = job?.details || {};
              const customerName = (
                details.customer_name || details.account_name || ""
              ).toLowerCase();
              return customerName.includes(searchLower);
            });
          }

          // Apply sync type filter before counting toward the page, so we fetch additional pages if many are filtered out
          const syncTypeFilter = (filtersRef.current.syncType || "").trim();
          if (syncTypeFilter) {
            failedJobs = failedJobs.filter((job) => {
              const { hasGL, hasTB } = getFailedSyncTypes(job);
              if (syncTypeFilter === "trial_balance") {
                return hasTB && !hasGL;
              }
              if (syncTypeFilter === "general_ledger") {
                return hasGL && !hasTB;
              }
              if (syncTypeFilter === "both") {
                return hasGL && hasTB;
              }
              return true;
            });
          }

          accumulated.push(...failedJobs);
          const discoveredCursor = extractCursor(responseNextUrl);
          if (!discoveredCursor) {
            nextCursor = null;
            break;
          }
          nextCursor = discoveredCursor;
        }

        setJobs(accumulated);
        setPagination({
          next: responseNextUrl,
          previous: null,
          count: null,
        });
        setPage(pageOverride);
        setPageCursors((prev) => {
          const updated = [...prev];
          updated[pageOverride] = cursor || null;
          return updated.slice(0, pageOverride + 1);
        });

        const missingIds = new Set();
        accumulated.forEach((job) => {
          const accounts = Array.isArray(job?.customer_accounts)
            ? job.customer_accounts.filter(Boolean)
            : [];
          const accountNameMap = new Map(
            accounts.map((acct) => [String(acct.id || ""), acct.name])
          );
          const details = job?.details || {};
          const results = job?.results || {};
          const failedIds = getFailedAccounts(job);

          failedIds.forEach((rawId) => {
            const id = String(rawId);
            if (!id) {
              return;
            }
            const hasName =
              (accountNameMap.has(id) && accountNameMap.get(id)) ||
              details.customer_name ||
              details.account_name ||
              results?.[id]?.customerDetails?.name ||
              nameCacheRef.current[id];
            if (!hasName) {
              missingIds.add(id);
            }
          });
        });
        const missingList = Array.from(missingIds);
        if (missingList.length) {
          const responses = await Promise.allSettled(
            missingList.map((id) => quickbooksService.getCustomerById(id))
          );
          const fetched = {};
          responses.forEach((res, idx) => {
            if (res.status === "fulfilled" && res.value?.data?.name) {
              const id = String(missingList[idx]);
              fetched[id] = res.value.data.name;
            }
          });
          if (Object.keys(fetched).length) {
            nameCacheRef.current = { ...nameCacheRef.current, ...fetched };
            setCacheVersion((prev) => prev + 1);
          }
        }
      } catch (err) {
        setError(
          err?.response?.data?.error ||
            err?.response?.data?.errors?.[0] ||
            "Failed to load error jobs."
        );
      } finally {
        setLoading(false);
      }
    },
    [resolveAccountName]
  );

  // Handle search change - matches UsersTab/AccountsTab pattern
  const handleSearchChange = useCallback(
    (searchValue) => {
      if (searchValue === searchTerm) {
        if (!searchValue || !searchValue.trim()) {
          setPage(0);
          setPageCursors([null]);
          fetchErrorJobs({ cursor: null, pageOverride: 0, search: "" });
        }
        return;
      }
      setSearchTerm(searchValue);
      setPage(0);
      setPageCursors([null]);
    },
    [searchTerm, fetchErrorJobs]
  );

  // Initial load and when searchTerm changes
  useEffect(() => {
    const searchTrimmed = searchTerm ? searchTerm.trim() : "";
    fetchErrorJobs({ cursor: null, pageOverride: 0, search: searchTrimmed });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]); // Only depend on searchTerm - fetchErrorJobs is stable

  const handleChangePage = (event, newPage) => {
    if (newPage === page || loading) return;
    if (newPage > page) {
      const cursor = extractCursor(pagination.next);
      fetchErrorJobs({ cursor: cursor || null, pageOverride: newPage, search: searchTerm });
    } else {
      const cursor = pageCursors[newPage] || null;
      fetchErrorJobs({ cursor, pageOverride: newPage, search: searchTerm });
    }
  };

  const handleChangeRowsPerPage = (event) => {
    const value = parseInt(event.target.value, 10);
    rowsPerPageRef.current = value;
    setRowsPerPage(value);
    setPageCursors([null]);
    fetchErrorJobs({ cursor: null, pageOverride: 0, search: searchTerm });
  };

  const filteredJobs = useMemo(() => {
    let list = jobs;
    if (filters.syncType) {
      list = list.filter((job) => {
        const { hasGL, hasTB } = getFailedSyncTypes(job);
        if (filters.syncType === "trial_balance") {
          return hasTB && !hasGL;
        }
        if (filters.syncType === "general_ledger") {
          return hasGL && !hasTB;
        }
        if (filters.syncType === "both") {
          return hasGL && hasTB;
        }
        return true;
      });
    }
    if (filters.initiatedAfter) {
      list = list.filter(
        (job) => job.started_at && job.started_at >= filters.initiatedAfter
      );
    }
    if (filters.initiatedBefore) {
      list = list.filter(
        (job) => job.started_at && job.started_at <= filters.initiatedBefore
      );
    }
    return list;
  }, [jobs, filters.syncType, filters.initiatedAfter, filters.initiatedBefore]);

  // Handlers for Initiated Date filter
  const handleInitiatedDateRangeSelection = (value) => {
    if (value === "custom") {
      setInitiatedCustomDateDialogOpen(true);
      return;
    }
    setInitiatedDateRange(value);
    setInitiatedCustomStartDate("");
    setInitiatedCustomEndDate("");
  };

  const handleInitiatedCustomDateRangeClose = () => {
    setInitiatedCustomDateDialogOpen(false);
    if (!initiatedCustomStartDate || !initiatedCustomEndDate) {
      setInitiatedDateRange("");
    }
  };

  const handleInitiatedCustomDateRangeApply = (startDate, endDate) => {
    setInitiatedCustomStartDate(startDate);
    setInitiatedCustomEndDate(endDate);
    setInitiatedDateRange("custom");
    setInitiatedCustomDateDialogOpen(false);
  };

  const handleInitiatedFilterApply = () => {
    // Apply date filter if date range is selected
    let initiatedAfter = "";
    let initiatedBefore = "";
    
    if (initiatedDateRange === "custom" && initiatedCustomStartDate && initiatedCustomEndDate) {
      initiatedAfter = initiatedCustomStartDate;
      initiatedBefore = initiatedCustomEndDate;
    } else if (initiatedDateRange) {
      const now = new Date();
      let startDate = new Date();
      
      switch (initiatedDateRange) {
        case "24h":
          startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
          break;
        case "7d":
          startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          break;
        case "30d":
          startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          break;
        default:
          break;
      }
      if (initiatedDateRange !== "") {
        initiatedAfter = startDate.toISOString().split("T")[0];
      }
    }

    const updated = {
      ...filtersRef.current,
      initiatedAfter,
      initiatedBefore,
    };
    filtersRef.current = updated;
    setFilters(updated);
    setPage(0);
    setPageCursors([null]);
    fetchErrorJobs({ cursor: null, pageOverride: 0, search: searchTerm });
    closeFilter("initiated");
  };

  const handleInitiatedFilterClear = () => {
    setInitiatedSortOption("");
    setInitiatedDateRange("");
    setInitiatedCustomStartDate("");
    setInitiatedCustomEndDate("");
    const updated = {
      ...filtersRef.current,
      initiatedAfter: "",
      initiatedBefore: "",
    };
    filtersRef.current = updated;
    setFilters(updated);
    setPage(0);
    setPageCursors([null]);
    fetchErrorJobs({ cursor: null, pageOverride: 0, search: searchTerm });
    closeFilter("initiated");
  };

  const totalCount = useMemo(() => {
    if (typeof pagination.count === "number" && pagination.count !== null) {
      return pagination.count;
    }
    if (pagination.next) {
      return (page + 2) * rowsPerPage;
    }
    return page * rowsPerPage + filteredJobs.length;
  }, [pagination.count, pagination.next, page, rowsPerPage, filteredJobs.length]);

  const renderFailedSyncTypes = (job) => {
    const { hasGL, hasTB } = getFailedSyncTypes(job);
    return (
      <Stack direction="row" spacing={1} alignItems="center">
        {hasGL && (
          <Chip
            label="General Ledger"
            size="small"
            sx={{ backgroundColor: "#fee2e2", color: "#b91c1c", fontWeight: 500 }}
          />
        )}
        {hasTB && (
          <Chip
            label="Trial Balance"
            size="small"
            sx={{ backgroundColor: "#fee2e2", color: "#b91c1c", fontWeight: 500 }}
          />
        )}
        {!hasGL && !hasTB && <Typography variant="body2">—</Typography>}
      </Stack>
    );
  };

  const renderFailedAccountsSummary = (job) => {
    const failed = getFailedAccounts(job);
    if (failed.length === 0) {
      return "—";
    }
    if (failed.length === 1) {
      const id = failed[0];
      return resolveAccountName(job, id);
    }
    return `${failed.length} Accounts`;
  };

  const handleDetails = (job, accountId = null) => {
    setSelectedJobId(job.id);
    const failed = getFailedAccounts(job);
    const targetAccountId = accountId || (failed.length === 1 ? failed[0] : null);
    setSelectedErrorCustomerId(targetAccountId);
    setSelectedErrorAccountName(resolveAccountName(job, targetAccountId));
    setErrorDetailsDialogOpen(true);
  };

  const renderActions = (job, isExpandedRow = false, accountId = null, onExpand) => {
    const failed = getFailedAccounts(job);
    const single = failed.length === 1;
    const retryLabel = single ? "Retry" : "Retry All";
    const detailsLabel = isExpandedRow ? "Error Details" : "Details";
    const isExpandable = !isExpandedRow && failed.length > 1 && typeof onExpand === "function";
    const handlePrimaryAction = () => {
      if (isExpandable) {
        onExpand();
        return;
      }
      handleDetails(job, accountId);
    };
    return (
      <Stack direction="row" spacing={1}>
        <Button
          size="small"
          variant="outlined"
          onClick={handlePrimaryAction}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderColor: "#627CFB",
            color: "#627CFB",
            "&:hover": { borderColor: "#4f46e5", color: "#4f46e5" },
          }}
        >
          {detailsLabel}
        </Button>
        <Button
          size="small"
          variant="contained"
          sx={{
            textTransform: "none",
            fontWeight: 600,
            backgroundColor: "#627CFB",
            "&:hover": { backgroundColor: "#4f46e5" },
          }}
        >
          {retryLabel}
        </Button>
      </Stack>
    );
  };

  const renderExpandedRows = (job) => {
    const failed = getFailedAccounts(job);
    if (!failed.length) {
      return (
        <Typography variant="body2" color="text.secondary">
          No failed accounts.
        </Typography>
      );
    }

    const rows = failed.map((id) => {
      const results = job?.results || {};
      const accountResult = results?.[id] || {};
      const glStatus = accountResult?.general_ledger?.status;
      const tbStatus = accountResult?.trial_balance?.status;
      const glFailed =
        glStatus && !["success", "completed"].includes(glStatus);
      const tbFailed =
        tbStatus && !["success", "completed"].includes(tbStatus);
      const syncLabel = glFailed && tbFailed
        ? "General Ledger, Trial Balance"
        : glFailed
        ? "General Ledger"
        : tbFailed
        ? "Trial Balance"
        : "—";

      return { id, name: resolveAccountName(job, id), glFailed, tbFailed, syncLabel };
    });

    const failedChip = (
      <Chip
        label="Failed"
        size="small"
        sx={{ backgroundColor: "#fee2e2", color: "#b91c1c", fontWeight: 500 }}
      />
    );
    const successChip = (
      <Chip
        label="Success"
        size="small"
        sx={{ backgroundColor: "#dcfce7", color: "#166534", fontWeight: 500 }}
      />
    );

    return (
      <TableContainer sx={{ width: "100%", display: "block" }}>
        <Table
          size="small"
          sx={{
            width: "100%",
            "& td, & th": {
              borderBottom: "1px solid #E5E7EB",
            },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, width: "20%" }}>Failed Accounts:</TableCell>
              <TableCell sx={{ fontWeight: 700, width: "25%" }}>Sync Types:</TableCell>
              <TableCell sx={{ fontWeight: 700, width: "15%" }}>General Ledger:</TableCell>
              <TableCell sx={{ fontWeight: 700, width: "15%" }}>Trial Balance:</TableCell>
              <TableCell sx={{ fontWeight: 700, width: "25%" }}>Actions:</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id}>
                <TableCell sx={{ width: "20%" }}>{row.name}</TableCell>
                <TableCell sx={{ width: "25%" }}>{row.syncLabel}</TableCell>
                <TableCell sx={{ width: "15%" }}>{row.glFailed ? failedChip : successChip}</TableCell>
                <TableCell sx={{ width: "15%" }}>{row.tbFailed ? failedChip : successChip}</TableCell>
                <TableCell sx={{ width: "25%" }}>
                  <Stack direction="row" spacing={1}>
                    <Button
                      size="small"
                      variant="outlined"
                      sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        borderColor: "#627CFB",
                        color: "#627CFB",
                        "&:hover": { borderColor: "#4f46e5", color: "#4f46e5" },
                      }}
                      onClick={() => handleDetails(job, row.id)}
                    >
                      Error Details
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        backgroundColor: "#627CFB",
                        "&:hover": { backgroundColor: "#4f46e5" },
                      }}
                    >
                      Retry
                    </Button>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderContent = () => (
    <Box sx={{ px: 0, pt: 0, pb: 4 }}>
      {error && (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    )}

      <Popover
        open={Boolean(filterAnchors.syncTypes)}
        anchorEl={filterAnchors.syncTypes}
        onClose={() => closeFilter("syncTypes")}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        disablePortal={false}
        slotProps={{
          root: { sx: { zIndex: 200000 } },
          paper: {
            sx: {
              zIndex: 200001,
              p: 2,
              borderRadius: 2,
              border: "1px solid #E5E7EB",
              boxShadow: "0px 12px 36px rgba(15, 23, 42, 0.18)",
              minWidth: 260,
            },
          },
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Filter Sync Types
            </Typography>
            <IconButton
              size="small"
              onClick={() => closeFilter("syncTypes")}
              sx={{ color: "#6B7280" }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>
          <Divider />
          <FormControl size="small" fullWidth>
            <InputLabel id="sync-types-label">Sync Types</InputLabel>
            <Select
              labelId="sync-types-label"
              label="Sync Types"
              value={pendingSyncType}
              onChange={(e) => setPendingSyncType(e.target.value)}
              MenuProps={{
                sx: {
                  zIndex: 200010,
                  "& .MuiPaper-root": { zIndex: 200011 },
                },
              }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="trial_balance">Trial Balance</MenuItem>
              <MenuItem value="both">Trial Balance & General Ledger</MenuItem>
              <MenuItem value="general_ledger">General Ledger</MenuItem>
            </Select>
          </FormControl>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button
              variant="text"
              size="small"
              onClick={() => {
                const updated = { ...filtersRef.current, syncType: "" };
                setPendingSyncType("");
                filtersRef.current = updated;
                setFilters(updated);
                setPage(0);
                setPageCursors([null]);
                fetchErrorJobs({ cursor: null, pageOverride: 0, search: searchTerm });
                closeFilter("syncTypes");
              }}
              sx={{ color: "#627CFB", textTransform: "none", fontWeight: 500 }}
            >
              Clear
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={() => {
                const updated = { ...filtersRef.current, syncType: pendingSyncType };
                filtersRef.current = updated;
                setFilters(updated);
                setPage(0);
                setPageCursors([null]);
                fetchErrorJobs({ cursor: null, pageOverride: 0, search: searchTerm });
                closeFilter("syncTypes");
              }}
              sx={{
                backgroundColor: "#627CFB",
                color: "white",
                textTransform: "none",
                fontWeight: 600,
                "&:hover": { backgroundColor: "#4f46e5" },
              }}
            >
              Filter
            </Button>
          </Box>
        </Box>
      </Popover>

      <Popover
        open={Boolean(filterAnchors.failedAccounts)}
        anchorEl={filterAnchors.failedAccounts}
        onClose={() => closeFilter("failedAccounts")}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        disablePortal={false}
        slotProps={{
          root: { sx: { zIndex: 200000 } },
          paper: {
            sx: {
              zIndex: 200001,
              p: 2,
              borderRadius: 2,
              border: "1px solid #E5E7EB",
              boxShadow: "0px 12px 36px rgba(15, 23, 42, 0.18)",
              minWidth: 320,
            },
          },
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Filter Failed Accounts
            </Typography>
            <IconButton
              size="small"
              onClick={() => closeFilter("failedAccounts")}
              sx={{ color: "#6B7280" }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>
          <Divider />
          <TextField
            size="small"
            placeholder="Search account name"
            value={pendingFailedAccount}
            onChange={(e) => setPendingFailedAccount(e.target.value)}
            fullWidth
          />
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button
              variant="text"
              size="small"
              onClick={() => {
                const updated = { ...filtersRef.current, failedAccount: "" };
                setPendingFailedAccount("");
                filtersRef.current = updated;
                setFilters(updated);
                setPage(0);
                setPageCursors([null]);
                fetchErrorJobs({ cursor: null, pageOverride: 0, search: searchTerm });
                closeFilter("failedAccounts");
              }}
              sx={{ color: "#627CFB", textTransform: "none", fontWeight: 500 }}
            >
              Clear
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={() => {
                const updated = { ...filtersRef.current, failedAccount: pendingFailedAccount };
                filtersRef.current = updated;
                setFilters(updated);
                setPage(0);
                setPageCursors([null]);
                fetchErrorJobs({ cursor: null, pageOverride: 0, search: searchTerm });
                closeFilter("failedAccounts");
              }}
              sx={{
                backgroundColor: "#627CFB",
                color: "white",
                textTransform: "none",
                fontWeight: 600,
                "&:hover": { backgroundColor: "#4f46e5" },
              }}
            >
              Filter
            </Button>
          </Box>
        </Box>
      </Popover>

      <Popover
        open={Boolean(filterAnchors.initiated)}
        anchorEl={filterAnchors.initiated}
        onClose={() => closeFilter("initiated")}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        disablePortal={false}
        slotProps={{
          root: { sx: { zIndex: 200000 } },
          paper: {
            sx: {
              zIndex: 200001,
              p: 2,
              borderRadius: "8px",
              border: "1px solid #E5E7EB",
              boxShadow: "0px 12px 36px rgba(15, 23, 42, 0.18)",
              minWidth: 500,
            },
          },
        }}
      >
        <Box sx={{ p: 2, minWidth: 500 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: "bold", color: "#111827" }}>
              Filter Initiated Date
            </Typography>
            <IconButton 
              size="small" 
              onClick={() => closeFilter("initiated")}
              sx={{ p: 0.5 }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>
          
          <Divider sx={{ mb: 2 }} />
          
          <Box sx={{ display: "flex", gap: 3 }}>
            {/* Left Side - Sort */}
            <Box sx={{ flex: 1 }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  fontWeight: "bold",
                  color: initiatedSortOption ? "#627CFB" : "#111827",
                  fontSize: "0.75rem",
                  mb: 1
                }}
              >
                Sort by
              </Typography>

              <FormControl fullWidth size="small" sx={{ zIndex: 200010 }}>
                <InputLabel>Select Sort Option</InputLabel>
                <Select
                  value={initiatedSortOption}
                  onChange={(e) => {
                    setInitiatedSortOption(e.target.value);
                  }}
                  label="Select Sort Option"
                  MenuProps={{ 
                    sx: { 
                      zIndex: 200010,
                      "& .MuiPaper-root": {
                        zIndex: 200011,
                        minWidth: "200px"
                      }
                    } 
                  }}
                  sx={{
                    "& .MuiOutlinedInput-notchedOutline": {
                      border: "none",
                      borderBottom: initiatedSortOption ? "2px solid #627CFB" : "2px solid #e5e7eb"
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      border: "none",
                      borderBottom: "2px solid #627CFB"
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      border: "none",
                      borderBottom: "2px solid #627CFB"
                    }
                  }}
                >
                  <MenuItem value="asc">Newest to Oldest</MenuItem>
                  <MenuItem value="desc">Oldest to Newest</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Right Side - Date Range */}
            <Box sx={{ flex: 1 }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  fontWeight: "bold",
                  color: (initiatedDateRange || initiatedCustomStartDate || initiatedCustomEndDate || filters.initiatedAfter || filters.initiatedBefore) ? "#627CFB" : "#111827",
                  fontSize: "0.75rem",
                  mb: 1
                }}
              >
                Date range
              </Typography>

              <FormControl fullWidth size="small" sx={{ zIndex: 200010 }}>
                <InputLabel>Select Date Range</InputLabel>
                <Select
                  value={initiatedDateRange}
                  onChange={(e) => {
                    handleInitiatedDateRangeSelection(e.target.value);
                  }}
                  label="Select Date Range"
                  MenuProps={{ 
                    sx: { 
                      zIndex: 200010,
                      "& .MuiPaper-root": {
                        zIndex: 200011,
                        minWidth: "200px"
                      }
                    } 
                  }}
                  sx={{
                    "& .MuiOutlinedInput-notchedOutline": {
                      border: "none",
                      borderBottom: (initiatedDateRange || initiatedCustomStartDate || initiatedCustomEndDate || filters.initiatedAfter || filters.initiatedBefore) ? "2px solid #627CFB" : "2px solid #e5e7eb"
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      border: "none",
                      borderBottom: "2px solid #627CFB"
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      border: "none",
                      borderBottom: "2px solid #627CFB"
                    }
                  }}
                >
                  <MenuItem value="24h">Last 24 hours</MenuItem>
                  <MenuItem value="7d">Last 7 Days</MenuItem>
                  <MenuItem value="30d">Last 30 Days</MenuItem>
                  <Divider />
                  <MenuItem value="custom">Custom range</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
            <Button 
              onClick={() => closeFilter("initiated")}
              size="small"
              sx={{ 
                color: "#627CFB",
                textTransform: "none",
                fontWeight: 500
              }}
            >
              Cancel
            </Button>
            {(initiatedSortOption || initiatedDateRange || initiatedCustomStartDate || initiatedCustomEndDate || filters.initiatedAfter || filters.initiatedBefore) && (
              <Button 
                onClick={handleInitiatedFilterClear}
                size="small"
                sx={{ 
                  color: "#627CFB",
                  textTransform: "none",
                  fontWeight: 500
                }}
              >
                Clear
              </Button>
            )}
            <Button 
              onClick={handleInitiatedFilterApply}
              variant="contained"
              size="small"
              sx={{ 
                backgroundColor: "#627CFB",
                textTransform: "none",
                fontWeight: 500,
                "&:hover": {
                  backgroundColor: "#4f46e5"
                }
              }}
            >
              Apply
            </Button>
          </Box>
        </Box>
      </Popover>
      <Paper
        sx={{
          border: "1px solid #E5E7EB",
          boxShadow: "none",
          borderRadius: "16px",
          overflow: "hidden",
        }}
      >
        <TableContainer>
          <Table
            size="small"
            sx={{
              tableLayout: "fixed",
            }}
          >
            <TableHead>
              <TableRow sx={{ backgroundColor: "#EEF0FD" }}>
                <TableCell
                  sx={{
                    padding: "12px 16px",
                    fontWeight: 600,
                    color: "#374151",
                    borderBottom: "1px solid #E5E7EB",
                    borderRight: "1px solid #E5E7EB",
                    width: columnWidths.job_id || 200,
                    minWidth: columnWidths.job_id || 200,
                    maxWidth: columnWidths.job_id || "none",
                    position: "relative",
                  }}
                >
                  Job ID
                  <Box
                    onMouseDown={(e) => handleMouseDown(e, "job_id")}
                    sx={{
                      position: "absolute",
                      right: 0,
                      top: 0,
                      bottom: 0,
                      width: "5px",
                      cursor: "col-resize",
                      userSelect: "none",
                      borderRight: "1px solid rgba(209, 213, 219, 0.6)",
                      "&:hover": {
                        borderRight: "2px solid #627CFB",
                      },
                      "&:active": {
                        borderRight: "2px solid #627CFB",
                      },
                    }}
                  />
                </TableCell>
                <TableCell
                  sx={{
                    padding: "12px 16px",
                    fontWeight: 600,
                    color: "#374151",
                    borderBottom: "1px solid #E5E7EB",
                    borderRight: "1px solid #E5E7EB",
                    width: columnWidths.failed_types || 220,
                    minWidth: columnWidths.failed_types || 220,
                    maxWidth: columnWidths.failed_types || "none",
                    position: "relative",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <span>Sync Types</span>
                    <IconButton
                      size="small"
                      onClick={(e) => openFilter("syncTypes", e)}
                      sx={{ color: "#6B7280", "&:hover": { color: "#4b5563" } }}
                    >
                      <FilterList fontSize="small" />
                    </IconButton>
                  </Box>
                  <Box
                    onMouseDown={(e) => handleMouseDown(e, "failed_types")}
                    sx={{
                      position: "absolute",
                      right: 0,
                      top: 0,
                      bottom: 0,
                      width: "5px",
                      cursor: "col-resize",
                      userSelect: "none",
                      borderRight: "1px solid rgba(209, 213, 219, 0.6)",
                      "&:hover": {
                        borderRight: "2px solid #627CFB",
                      },
                      "&:active": {
                        borderRight: "2px solid #627CFB",
                      },
                    }}
                  />
                </TableCell>
                <TableCell
                  sx={{
                    padding: "12px 16px",
                    fontWeight: 600,
                    color: "#374151",
                    borderBottom: "1px solid #E5E7EB",
                    borderRight: "1px solid #E5E7EB",
                    width: columnWidths.started_at || 180,
                    minWidth: columnWidths.started_at || 180,
                    maxWidth: columnWidths.started_at || "none",
                    position: "relative",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <span>Initiated Date</span>
                    <IconButton
                      size="small"
                      onClick={(e) => openFilter("initiated", e)}
                      sx={{ color: "#6B7280", "&:hover": { color: "#4b5563" }, p: 0.5 }}
                    >
                      <FilterList fontSize="small" />
                    </IconButton>
                  </Box>
                  <Box
                    onMouseDown={(e) => handleMouseDown(e, "started_at")}
                    sx={{
                      position: "absolute",
                      right: 0,
                      top: 0,
                      bottom: 0,
                      width: "5px",
                      cursor: "col-resize",
                      userSelect: "none",
                      borderRight: "1px solid rgba(209, 213, 219, 0.6)",
                      "&:hover": {
                        borderRight: "2px solid #627CFB",
                      },
                      "&:active": {
                        borderRight: "2px solid #627CFB",
                      },
                    }}
                  />
                </TableCell>
                <TableCell
                  sx={{
                    padding: "12px 16px",
                    fontWeight: 600,
                    color: "#374151",
                    borderBottom: "1px solid #E5E7EB",
                    borderRight: "1px solid #E5E7EB",
                    width: columnWidths.failed_accounts || 200,
                    minWidth: columnWidths.failed_accounts || 200,
                    maxWidth: columnWidths.failed_accounts || "none",
                    position: "relative",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <span>Failed Accounts</span>
                    <IconButton
                      size="small"
                      onClick={(e) => openFilter("failedAccounts", e)}
                      sx={{ color: "#6B7280", "&:hover": { color: "#4b5563" } }}
                    >
                      <FilterList fontSize="small" />
                    </IconButton>
                  </Box>
                  <Box
                    onMouseDown={(e) => handleMouseDown(e, "failed_accounts")}
                    sx={{
                      position: "absolute",
                      right: 0,
                      top: 0,
                      bottom: 0,
                      width: "5px",
                      cursor: "col-resize",
                      userSelect: "none",
                      borderRight: "1px solid rgba(209, 213, 219, 0.6)",
                      "&:hover": {
                        borderRight: "2px solid #627CFB",
                      },
                      "&:active": {
                        borderRight: "2px solid #627CFB",
                      },
                    }}
                  />
                </TableCell>
                <TableCell
                  sx={{
                    padding: "12px 16px",
                    fontWeight: 600,
                    color: "#374151",
                    borderBottom: "1px solid #E5E7EB",
                    width: columnWidths.actions || 120,
                    minWidth: columnWidths.actions || 120,
                    maxWidth: columnWidths.actions || "none",
                    position: "relative",
                  }}
                >
                  Actions
                  <Box
                    onMouseDown={(e) => handleMouseDown(e, "actions")}
                    sx={{
                      position: "absolute",
                      right: 0,
                      top: 0,
                      bottom: 0,
                      width: "5px",
                      cursor: "col-resize",
                      userSelect: "none",
                      borderRight: "1px solid rgba(209, 213, 219, 0.6)",
                      "&:hover": {
                        borderRight: "2px solid #627CFB",
                      },
                      "&:active": {
                        borderRight: "2px solid #627CFB",
                      },
                    }}
                  />
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && filteredJobs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : filteredJobs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                    <Typography variant="body2" color="text.secondary">
                      {searchTerm.trim()
                        ? "No sync errors for your search."
                        : "No sync errors found."}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredJobs.map((job) => {
                  const failedAccounts = getFailedAccounts(job);
                  const isExpandable = failedAccounts.length > 1;
                  const isExpanded = expandedIds.has(job.id);
                  return (
                    <React.Fragment key={job.id}>
                      <TableRow hover sx={{ borderBottom: "1px solid #F3F4F6" }}>
                        <TableCell
                          sx={{
                            padding: "12px 16px",
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            width: columnWidths.job_id || 200,
                            minWidth: columnWidths.job_id || 200,
                            maxWidth: columnWidths.job_id || "none",
                          }}
                        >
                          {/*
                            Keep the toggle aligned but leave space for job id text/ellipsis.
                            Tooltip shows full job id.
                          */}
                          <IconButton
                            size="small"
                            disabled={!isExpandable}
                            onClick={
                              isExpandable ? () => toggleExpanded(job.id) : undefined
                            }
                            sx={{
                              color: "#6B7280",
                              visibility: isExpandable ? "visible" : "hidden",
                            }}
                          >
                            {isExpanded ? (
                              <ExpandLess fontSize="small" />
                            ) : (
                              <ExpandMore fontSize="small" />
                            )}
                          </IconButton>
                          <Tooltip
                            title={job.id || "—"}
                            enterDelay={400}
                            arrow
                            slotProps={{
                              tooltip: {
                                sx: {
                                  bgcolor: "#111827",
                                  color: "#fff",
                                  zIndex: 200002,
                                },
                              },
                              popper: {
                                sx: {
                                  zIndex: 200002,
                                },
                              },
                            }}
                          >
                            <span style={{ display: "inline-block", cursor: "default" }}>
                              <Typography
                                variant="body2"
                                component="span"
                                sx={{
                                  maxWidth: Math.max((columnWidths.job_id || 200) - 60, 80),
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  display: "block",
                                }}
                              >
                                {job.id || "—"}
                              </Typography>
                            </span>
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={{ padding: "12px 16px", width: 200, minWidth: 200 }}>
                          {renderFailedSyncTypes(job)}
                        </TableCell>
                        <TableCell sx={{ padding: "12px 16px", width: 180, minWidth: 180 }}>
                          {formatDateTime(job.started_at)}
                        </TableCell>
                        <TableCell sx={{ padding: "12px 16px", width: 200, minWidth: 200 }}>
                          {renderFailedAccountsSummary(job)}
                        </TableCell>
                        <TableCell sx={{ padding: "12px 16px", width: 120, minWidth: 120 }}>
                          {renderActions(
                            job,
                            false,
                            null,
                            isExpandable ? () => toggleExpanded(job.id) : undefined
                          )}
                        </TableCell>
                      </TableRow>
                      {isExpanded && (
                        <TableRow>
                          <TableCell colSpan={5} sx={{ backgroundColor: "#F9FAFB", p: 0 }}>
                            <Box sx={{ px: 3, py: 2 }}>
                              {renderExpandedRows(job)}
                            </Box>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[20, 40, 60, 80]}
        nextIconButtonProps={{ disabled: loading || !pagination.next }}
        backIconButtonProps={{ disabled: loading || page === 0 }}
        labelDisplayedRows={({ from, to }) => `${from}–${to}`}
        sx={{ borderTop: "1px solid #E5E7EB", mt: 1 }}
      />

      <QBErrorDetailsDialog
        open={errorDetailsDialogOpen}
        onClose={() => setErrorDetailsDialogOpen(false)}
        jobId={selectedJobId}
        customerId={selectedErrorCustomerId}
        accountName={selectedErrorAccountName}
      />

      <CustomDateRangeDialog
        open={initiatedCustomDateDialogOpen}
        onClose={handleInitiatedCustomDateRangeClose}
        onApply={handleInitiatedCustomDateRangeApply}
        initialStartDate={initiatedCustomStartDate}
        initialEndDate={initiatedCustomEndDate}
      />
    </Box>
  );

  return {
    searchTerm,
    handleSearchChange,
    loading,
    error,
    renderContent,
  };
}
